/*
 * collection-scroll-memory.js
 * ---------------------------------------------------------------------------
 * Fixes: after tapping a product and pressing Back (or the breadcrumb link),
 * the collection reloaded to the top / only showed one page of products.
 *
 * How it works:
 *  - When you click a product card on a collection, we remember your exact
 *    scroll position, which card you tapped, and which infinite-scroll pages
 *    were loaded at that moment (keyed to that collection + its filters/sort).
 *  - When you come back, we re-fetch the pages that are missing (using the
 *    theme's own Section Rendering API), stitch them back into the grid in the
 *    right order, then scroll you to the exact card you tapped.
 *
 * Plain classic script (no ES module) so it runs independently of the theme
 * import map. Safe to no-op on themes without infinite scroll.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var KEY_PREFIX = 'lh-col-scroll:';
  var MAX_AGE_MS = 30 * 60 * 1000; // forget saved spots after 30 minutes
  var MAX_FETCH_PAGES = 12; // safety cap on how many pages we'll rebuild
  var CARD_SELECTOR = '[ref="cards[]"]';

  /* ---- helpers ---------------------------------------------------------- */

  // A stable key per collection view: pathname + filters/sort, but WITHOUT
  // the page or section_id params, so all infinite-scroll pages share one key.
  function baseKey() {
    try {
      var u = new URL(window.location.href);
      u.searchParams.delete('page');
      u.searchParams.delete('section_id');
      u.searchParams.sort();
      return KEY_PREFIX + u.pathname + '?' + u.searchParams.toString();
    } catch (e) {
      return KEY_PREFIX + window.location.pathname;
    }
  }

  function getResultsList() {
    return document.querySelector('results-list[section-id]');
  }

  function getGrid() {
    return document.querySelector('[ref="grid"]');
  }

  function isCollectionPage() {
    return !!getResultsList() && !!getGrid();
  }

  function pagesInDom() {
    var map = {};
    var cards = document.querySelectorAll(CARD_SELECTOR + '[data-page]');
    for (var i = 0; i < cards.length; i++) {
      map[Number(cards[i].getAttribute('data-page'))] = true;
    }
    return map;
  }

  /* ---- SAVE: remember the spot when a product card is clicked ------------ */

  function attachSaver() {
    // Capture phase so we record before the theme's own click handlers /
    // navigation fire. We never call preventDefault — navigation proceeds.
    document.addEventListener(
      'click',
      function (event) {
        if (event.defaultPrevented) return;
        var target = event.target;
        if (!target || !target.closest) return;

        // Only care about links that go to a product.
        var link = target.closest('a[href]');
        if (!link) return;
        var href = link.getAttribute('href') || '';
        if (href.indexOf('/products/') === -1) return;

        var card = link.closest(CARD_SELECTOR);
        if (!card || !card.id) return;

        var results = getResultsList();
        var pageList = Object.keys(pagesInDom())
          .map(Number)
          .sort(function (a, b) {
            return a - b;
          });

        var payload = {
          cardId: card.id,
          y: window.scrollY,
          cardTop: card.getBoundingClientRect().top, // viewport offset at click
          pages: pageList,
          sectionId: results ? results.getAttribute('section-id') : null,
          ts: Date.now()
        };

        try {
          sessionStorage.setItem(baseKey(), JSON.stringify(payload));
        } catch (e) {
          /* storage full / disabled — nothing we can do, fail quietly */
        }
      },
      true
    );
  }

  /* ---- RESTORE: rebuild pages and scroll back --------------------------- */

  function readState() {
    try {
      var raw = sessionStorage.getItem(baseKey());
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || typeof data.ts !== 'number' || Date.now() - data.ts > MAX_AGE_MS) {
        sessionStorage.removeItem(baseKey());
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(baseKey());
    } catch (e) {}
  }

  function sectionFetchURL(sectionId, page) {
    var u = new URL(window.location.href);
    u.searchParams.set('section_id', sectionId);
    u.searchParams.set('page', String(page));
    u.hash = '';
    return u.toString();
  }

  function fetchPageCards(sectionId, page) {
    return fetch(sectionFetchURL(sectionId, page), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        if (!html) return [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var grid = doc.querySelector('[ref="grid"]');
        if (!grid) return [];
        return Array.prototype.slice.call(grid.querySelectorAll(':scope > ' + CARD_SELECTOR));
      })
      .catch(function () {
        return [];
      });
  }

  function scrollToTarget(data) {
    // Two frames: let inserted cards lay out first.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var card = document.getElementById(data.cardId);
        if (card) {
          // Put the card back at the same viewport offset it had at click time.
          var top = card.getBoundingClientRect().top + window.scrollY - (data.cardTop || 0);
          window.scrollTo(0, Math.max(0, top));
        } else if (typeof data.y === 'number') {
          window.scrollTo(0, data.y);
        }
        clearState();
      });
    });
  }

  function restore(data) {
    var results = getResultsList();
    var grid = getGrid();
    if (!results || !grid) {
      clearState();
      return;
    }

    var sectionId = data.sectionId || results.getAttribute('section-id');
    if (!sectionId) {
      clearState();
      return;
    }

    // Stop the browser's own (broken) scroll restoration for this navigation.
    try {
      history.scrollRestoration = 'manual';
    } catch (e) {}

    var have = pagesInDom();
    var presentNums = Object.keys(have).map(Number);
    var minPresent = presentNums.length ? Math.min.apply(null, presentNums) : Infinity;
    var maxPresent = presentNums.length ? Math.max.apply(null, presentNums) : -Infinity;

    var need = (data.pages || [])
      .filter(function (p) {
        return !have[p];
      })
      .slice(0, MAX_FETCH_PAGES);

    if (!need.length) {
      scrollToTarget(data);
      return;
    }

    Promise.all(
      need.map(function (p) {
        return fetchPageCards(sectionId, p).then(function (cards) {
          return { page: p, cards: cards };
        });
      })
    )
      .then(function (fetched) {
        fetched.sort(function (a, b) {
          return a.page - b.page;
        });

        var firstCard = grid.querySelector(':scope > ' + CARD_SELECTOR);

        fetched.forEach(function (item) {
          if (!item.cards.length) return;
          if (item.page < minPresent && firstCard) {
            // Pages loaded above the current first card — prepend, in order.
            item.cards.forEach(function (c) {
              grid.insertBefore(c, firstCard);
            });
          } else {
            // Pages at/after the current content — append.
            item.cards.forEach(function (c) {
              grid.appendChild(c);
            });
          }
        });

        scrollToTarget(data);
      })
      .catch(function () {
        // Network hiccup: fall back to a plain scroll restore.
        scrollToTarget(data);
      });
  }

  /* ---- init ------------------------------------------------------------- */

  function init() {
    if (!isCollectionPage()) return;
    attachSaver();
    var data = readState();
    if (data) restore(data);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // If the browser restored the page from bfcache, the DOM and scroll are
  // already intact — just drop the saved spot so a later reload starts clean.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) clearState();
  });
})();